import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, History, RotateCcw } from "lucide-react";

const download = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/**
 * Label printing for blocks, slides, containers and send-out packages.
 * `items` are objects with { id, kind }; caseIds / sendoutIds print case-level labels.
 */
export const LabelPrintDialog = ({ open, onOpenChange, items = [], caseIds = [], sendoutIds = [],
                                   barcodes = [], defaultSpec = "slide", caseId = "", heading = "",
                                   extraSpec = "" }) => {
  const [meta, setMeta] = useState(null);
  const [spec, setSpec] = useState(defaultSpec);
  const [mode, setMode] = useState("roll");
  const [copies, setCopies] = useState(1);
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState("");
  const [withExtra, setWithExtra] = useState(true);
  const [partCount, setPartCount] = useState(1);
  const [presets, setPresets] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/labels/history", { params: caseId ? { case_id: caseId } : { limit: 25 } });
      setHistory(data);
    } catch (e) { /* history is informational */ }
  }, [caseId]);

  useEffect(() => {
    if (!open) return;
    api.get("/labels/specs").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/lab").then((r) => setPresets(r.data.container_presets || [])).catch(() => {});
    loadHistory();
    setSpec(defaultSpec);
    setPartCount(1);
  }, [open, defaultSpec, loadHistory]);

  const partsAsked = Math.max(1, Math.min(Number(partCount) || 1, 26));
  const partLetters = LETTERS.slice(0, partsAsked);
  const perCase = caseIds.length > 0 ? partsAsked : 1;

  const print = async () => {
    setBusy("print");
    try {
      const specs = extraSpec && withExtra && spec !== extraSpec ? [spec, extraSpec] : [spec];
      for (const s of specs) {
        const res = await api.post("/labels/print", {
          spec: s, mode, copies: Number(copies) || 1, reason,
          specimen_ids: items.map((i) => i.id), barcodes,
          case_ids: caseIds, sendout_ids: sendoutIds,
          parts: s === "container" && partsAsked > 1 ? partLetters : [],
        }, { responseType: "blob" });
        download(res.data, `PathQity_${s}_labels.pdf`);
      }
      toast.success(specs.length > 1
        ? `${specs.length} label PDFs downloaded — print each at true size (no scaling)`
        : "Label PDF downloaded — send it to the label printer at true size (no scaling)");
      setReason("");
      loadHistory();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  const reprint = async (row) => {
    const why = window.prompt("Why is this being reprinted? (smudged, lost, damaged…)");
    if (!why) return;
    setBusy(row.id);
    try {
      const res = await api.post(`/labels/reprint/${row.id}`, null, {
        params: { reason: why }, responseType: "blob",
      });
      download(res.data, `PathQity_${row.spec}_reprint.pdf`);
      toast.success("Reprint downloaded and logged");
      loadHistory();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const total = (items.length + caseIds.length * perCase + sendoutIds.length + barcodes.length)
                * (Number(copies) || 1);
  const activeSpec = (meta?.specs || []).find((s) => s.key === spec);
  const showParts = spec === "container" && caseIds.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="label-print-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{heading || "Print labels"}</DialogTitle>
          <DialogDescription>
            Labels render at true physical size, so print at 100% with scaling switched off — this works on a
            Zebra or Dymo roll printer as well as a laser sheet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">Label size</Label>
            <select value={spec} onChange={(e) => setSpec(e.target.value)} data-testid="label-spec-select"
                    className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
              {(meta?.specs || []).map((s) => (
                <option key={s.key} value={s.key}>{`${s.label} — ${s.size}`}</option>
              ))}
            </select>
            {activeSpec && <p className="mt-1 text-xs text-slate-500">{activeSpec.note}</p>}
          </div>
          {showParts && (
            <div className="border border-slate-200 bg-slate-50 p-3" data-testid="label-parts-picker">
              <Label className="text-xs uppercase tracking-wide text-slate-600">
                How many containers came in?
              </Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Input type="number" min="1" max="26" value={partCount} data-testid="label-parts-input"
                       onChange={(e) => setPartCount(e.target.value)}
                       className="h-11 w-24 border-slate-300" />
                <span className="text-sm text-slate-600">parts</span>
                {presets.length > 0 && (
                  <select value="" data-testid="label-parts-preset-select"
                          onChange={(e) => e.target.value && setPartCount(Number(e.target.value))}
                          className="h-11 flex-1 rounded-md border border-slate-300 bg-white px-3 text-sm">
                    <option value="">Use a preset…</option>
                    {presets.map((p) => (
                      <option key={p.name} value={p.parts}>{`${p.name} — ${p.parts} part${p.parts === 1 ? "" : "s"}`}</option>
                    ))}
                  </select>
                )}
              </div>
              <p className="mt-2 text-xs text-slate-600" data-testid="label-parts-preview">
                {partsAsked > 1
                  ? `One label per part: ${partLetters.join(", ")} — each barcode is the accession plus its part letter.`
                  : "A single container label with no part letter."}
              </p>
            </div>
          )}
          {extraSpec && (
            <label className="flex items-start gap-2 border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <input type="checkbox" checked={withExtra} onChange={(e) => setWithExtra(e.target.checked)}
                     data-testid="label-extra-spec-checkbox" className="mt-0.5 h-4 w-4" />
              <span>
                Also print <span className="font-semibold">{extraSpec}</span> labels in the same go, so grossing
                has them ready. Each size downloads as its own PDF because the stock differs.
              </span>
            </label>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Output</Label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="label-mode-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="roll">Roll printer — one label per page</option>
                <option value="sheet">Letter sheet — grid with cut guides</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Copies of each</Label>
              <Input type="number" min="1" max="20" value={copies} data-testid="label-copies-input"
                     onChange={(e) => setCopies(e.target.value)} className="mt-1 h-11 border-slate-300" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">Note (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="label-reason-input"
                   placeholder="e.g. recuts for Dr. Moritz" className="mt-1 h-11 border-slate-300" />
          </div>
          <Button onClick={print} disabled={busy === "print" || total === 0} data-testid="label-print-submit"
                  className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Printer size={15} className="mr-2" />
            {busy === "print" ? "Building…" : `Print ${total} label${total === 1 ? "" : "s"}`}
          </Button>

          <button onClick={() => setShowHistory((s) => !s)} data-testid="label-history-toggle"
                  className="flex w-full items-center gap-2 border-t border-slate-200 pt-3 text-sm font-semibold text-[#134e4a]">
            <History size={14} /> Print history &amp; reprints ({history.length})
          </button>
          {showHistory && (
            <ul className="divide-y divide-slate-100 border border-slate-200" data-testid="label-history-list">
              {history.length === 0 && (
                <li className="px-3 py-4 text-sm text-slate-500">Nothing printed yet.</li>
              )}
              {history.map((h) => (
                <li key={h.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-xs"
                    data-testid={`label-history-${h.id}`}>
                  <span className="font-semibold text-slate-800">{h.spec_label}</span>
                  <span className="text-slate-500">
                    {h.count} × {h.copies} · {(h.accession_numbers || []).slice(0, 3).join(", ")}
                  </span>
                  {h.reprint_of && (
                    <span className="bg-amber-100 px-1.5 font-bold uppercase text-amber-900">reprint</span>
                  )}
                  <span className="text-slate-400">{fmtDateTime(h.printed_at)} · {h.printed_by}</span>
                  {h.reason && <span className="italic text-slate-500">“{h.reason}”</span>}
                  <Button size="sm" variant="outline" disabled={busy === h.id} onClick={() => reprint(h)}
                          data-testid={`label-reprint-${h.id}`} className="ml-auto h-7 border-slate-300 px-2">
                    <RotateCcw size={12} className="mr-1" /> Reprint
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LabelPrintDialog;
